import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import {
  GatewayActions,
  GatewayBrand,
  GatewayContent,
  GatewayShell,
  GatewaySpinner,
} from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRunOnceWhen } from '@/_shared/hooks/useRunOnceWhen';
import { orpc } from '@/_shared/lib/orpc';

import { LinkNotValid } from '../components/nativeSignIn/LinkNotValid';
import { MfaCodeStep } from '../components/nativeSignIn/MfaCodeStep';
import { MfaSetupRequired } from '../components/nativeSignIn/MfaSetupRequired';
import { RequireNativeSignIn } from '../components/nativeSignIn/RequireNativeSignIn';
import { useLinkParams } from '../hooks/useLinkParams';
import { isSigningIn, useSignIn } from '../hooks/useSignIn';
import { PASSWORD_RULE_HINT } from '../lib/authFormSchema';
import type { LoginCredentials, MfaChallenge } from '../lib/rudderstack-auth-client';
import {
  acceptRudderstackInvite,
  type RudderstackInviteCheck,
  validateRudderstackInvite,
} from '../lib/rudderstack-invite-client';
import { rudderstackInviteSchema, type RudderstackInviteValues } from '../lib/rudderstackInviteSchema';
import type { NativeSignInConfig } from '../types';

/** The web app's page for the same token, while Lookout's own sign-in is off. */
function webAppAcceptInviteLink(webAppUrl: string, token: string): string {
  return `${webAppUrl.replace(/\/+$/, '')}/acceptInvite?${new URLSearchParams({ token }).toString()}`;
}

/**
 * `/rudderstack-invite?token=…`: the page a RudderStack invitation from Lookout links to (ACT2-1124 LLD §5.3). It
 * works before sign-in. RudderStack checks the token: a new person sets a name and a password here, and Lookout then
 * signs them in with that password, as the web app does. Everyone else signs in. The sign-in opens the Lookout
 * workspace with the role of the invitation.
 */
export function RudderstackInvite(): React.ReactElement {
  usePageTitle('Accept invitation');
  // First, so the token leaves the URL before anything records it.
  const token = useLinkParams().get('token');

  return (
    <RequireNativeSignIn webAppLink={(webAppUrl) => (token ? webAppAcceptInviteLink(webAppUrl, token) : null)}>
      {(config) =>
        token ? <InviteFlow token={token} config={config} /> : <LinkNotValid actionLabel="Sign in" actionTo="/login" />
      }
    </RequireNativeSignIn>
  );
}

function InviteFlow({ token, config }: { token: string; config: NativeSignInConfig }): React.ReactElement {
  // Validate accepts the invitation for an existing person, so it runs once, not as a query that refetches.
  // The answer goes into state from the mutation's own callbacks. StrictMode mounts twice, and the second
  // mount's observer no longer hears the mutation that the first mount started.
  const [outcome, setOutcome] = useState<{ result?: RudderstackInviteCheck; error?: Error }>({});
  const check = useMutation({
    mutationFn: () => validateRudderstackInvite(token),
    gcTime: 0,
    onSuccess: (result) => setOutcome({ result }),
    onError: (error) => setOutcome({ error }),
  });
  const runCheck = (): void => {
    setOutcome({});
    check.mutate();
  };
  useRunOnceWhen(true, runCheck);

  if (outcome.error) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<CircleAlert />}
          variant="error"
          title="This page did not load"
          description={outcome.error.message}
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" loading={check.isPending} onClick={runCheck}>
            Try again
          </Button>
        </GatewayActions>
      </GatewayShell>
    );
  }
  if (!outcome.result) return <GatewaySpinner mode="fullScreen" label="Checking your invitation" />;
  return <InviteStep token={token} config={config} result={outcome.result} />;
}

function InviteStep({
  token,
  config,
  result,
}: {
  token: string;
  config: NativeSignInConfig;
  result: RudderstackInviteCheck;
}): React.ReactElement {
  if (result.status === 'INVALID') {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<CircleAlert />}
          variant="warning"
          title="This invitation is not valid"
          description={`${result.message} If the invitation was accepted already, sign in with the invited email to open the Lookout workspace.`}
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" asChild>
            <Link to="/login">Sign in</Link>
          </Button>
        </GatewayActions>
      </GatewayShell>
    );
  }
  if (result.status === 'PASSWORD_SIGNUP') {
    return <NewAccountStep token={token} config={config} email={result.email} />;
  }
  return (
    <SignInStep
      title={result.status === 'SSO_SIGNUP' ? 'Sign in with SSO to finish' : 'Invitation accepted'}
      email={result.email}
      description={
        result.status === 'SSO_SIGNUP'
          ? `Your organization signs in with SSO. Sign in as ${result.email} to open the Lookout workspace.`
          : `Sign in as ${result.email} to open the Lookout workspace.`
      }
    />
  );
}

/**
 * The last step for every status: sign in. A person who is signed in already continues, and gets the role now. When
 * that person is the invited email, the text says so (mini run of 28 Oct): it asked them to sign in before. When
 * another person is signed in, the main action signs them out, so that the invited person can sign in. Continue stays
 * as the second action: the invite can be to another proven email of the same person.
 */
function SignInStep({
  title,
  email,
  description,
}: {
  title: string;
  email: string;
  description: string;
}): React.ReactElement {
  const { user, signOutAndRedirect } = useAuth();
  const signedInAsInvitee = user?.email?.trim().toLowerCase() === email.trim().toLowerCase();
  // A person whose email Lookout does not know keeps Continue, as before: Lookout cannot tell who they are.
  const otherEmail = user?.email && !signedInAsInvitee ? user.email : null;
  const [signOut, setSignOut] = useState<'idle' | 'pending' | 'failed'>('idle');
  const switchAccount = (): void => {
    setSignOut('pending');
    void signOutAndRedirect().then((ended) => {
      if (!ended) setSignOut('failed');
    });
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const join = useMutation(
    orpc.siblingMembers.acceptInvitations.mutationOptions({
      onSuccess: async ({ joinedWorkspaceIds }) => {
        await queryClient.refetchQueries({ queryKey: orpc.auth.me.key() });
        // Open the workspace of the invitation, not the workspace that the person used last.
        const [joined] = joinedWorkspaceIds;
        void navigate(joined ? `/w/${joined}` : '/', { replace: true });
      },
    }),
  );

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent
        icon={<MailCheck />}
        variant="success"
        title={title}
        description={
          signedInAsInvitee
            ? 'Continue to open the Lookout workspace.'
            : otherEmail
              ? `You are signed in as ${otherEmail}. ${description}`
              : description
        }
      />
      {signOut === 'failed' ? (
        <Callout variant="error" role="alert" className="mt-4">
          Lookout could not sign you out. Try again.
        </Callout>
      ) : null}
      {join.error ? (
        <Callout variant="error" role="alert" className="mt-4">
          {join.error.message || 'Something went wrong. Please try again.'}
        </Callout>
      ) : null}
      <GatewayActions>
        {otherEmail ? (
          <>
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              loading={signOut === 'pending'}
              disabled={join.isPending}
              onClick={switchAccount}
            >
              Sign out
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              loading={join.isPending}
              disabled={signOut === 'pending'}
              onClick={() => join.mutate({})}
            >
              Continue anyway
            </Button>
          </>
        ) : user ? (
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={join.isPending}
            onClick={() => join.mutate({})}
          >
            Continue
          </Button>
        ) : (
          <Button variant="primary" size="lg" className="w-full" asChild>
            <Link to="/login">Sign in</Link>
          </Button>
        )}
      </GatewayActions>
    </GatewayShell>
  );
}

/**
 * The invitation of a person with no RudderStack account. While another person is signed in, the account form does not
 * show: that person signs out first, and the page opens again with the token, so the invited person makes the account.
 */
function NewAccountStep({
  token,
  config,
  email,
}: {
  token: string;
  config: NativeSignInConfig;
  email: string;
}): React.ReactElement {
  const { user, signOutAndRedirect } = useAuth();
  const [signOut, setSignOut] = useState<'idle' | 'pending' | 'failed'>('idle');
  if (!user) return <NewAccountForm token={token} config={config} email={email} />;

  const switchAccount = (): void => {
    setSignOut('pending');
    void signOutAndRedirect(`/rudderstack-invite?${new URLSearchParams({ token }).toString()}`).then((ended) => {
      if (!ended) setSignOut('failed');
    });
  };
  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent
        icon={<MailCheck />}
        variant="success"
        title="Set up the account of the invitation"
        description={`You are signed in as ${user.email ?? 'another person'}. The invitation is for ${email}, who has no RudderStack account yet. Sign out, so that ${email} can set up the account.`}
      />
      {signOut === 'failed' ? (
        <Callout variant="error" role="alert" className="mt-4">
          Lookout could not sign you out. Try again.
        </Callout>
      ) : null}
      <GatewayActions>
        <Button variant="primary" size="lg" className="w-full" loading={signOut === 'pending'} onClick={switchAccount}>
          Sign out
        </Button>
      </GatewayActions>
    </GatewayShell>
  );
}

type AccountStep =
  | { kind: 'form' }
  | { kind: 'mfa'; credentials: LoginCredentials; challenge: MfaChallenge }
  | { kind: 'mfa_setup' }
  | { kind: 'sign_in' };

function NewAccountForm({
  token,
  config,
  email,
}: {
  token: string;
  config: NativeSignInConfig;
  email: string;
}): React.ReactElement {
  const [step, setStep] = useState<AccountStep>({ kind: 'form' });
  const form = useForm<RudderstackInviteValues>({
    resolver: zodResolver(rudderstackInviteSchema),
    mode: 'onTouched',
    defaultValues: { name: '', password: '', confirmPassword: '' },
  });
  const accept = useMutation({
    mutationFn: (values: RudderstackInviteValues) =>
      acceptRudderstackInvite({ token, name: values.name, password: values.password }),
    gcTime: 0,
  });
  // No return path: the sign-in gives the invitation's Lookout role, and Lookout opens that workspace.
  const signIn = useSignIn(config.apiBaseUrl, null);
  const busy = accept.isPending || isSigningIn(signIn);
  // This component stays mounted after the form, so leaving the form clears the password from the form fields and
  // from both mutations (`gcTime: 0` then drops them from the cache). Only the MFA step keeps it, in its own state,
  // because it signs in with it again; leaving the MFA step drops that too.
  const goTo = (next: Exclude<AccountStep, { kind: 'form' }>): void => {
    form.reset();
    accept.reset();
    signIn.reset();
    setStep(next);
  };
  const toSignIn = (): void => goTo({ kind: 'sign_in' });

  // The account exists now. A failed sign-in falls back to the sign-in page, where the new password works.
  const signInNow = (password: string): void => {
    const credentials: LoginCredentials = { email, password };
    signIn.mutate(credentials, {
      // The MFA step takes the credentials from the callback's arguments: a closure over them would stay on the
      // mutation observer after the reset.
      onSuccess: (outcome, sent) => {
        if (outcome.kind === 'mfa_challenge') goTo({ kind: 'mfa', credentials: sent, challenge: outcome.challenge });
        if (outcome.kind === 'mfa_setup') goTo({ kind: 'mfa_setup' });
      },
      onError: toSignIn,
    });
  };

  if (step.kind === 'mfa') {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <MfaCodeStep
          apiBaseUrl={config.apiBaseUrl}
          credentials={step.credentials}
          challenge={step.challenge}
          returnTo={null}
          onSetupRequired={() => goTo({ kind: 'mfa_setup' })}
          onBack={toSignIn}
        />
      </GatewayShell>
    );
  }
  if (step.kind === 'mfa_setup') {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <MfaSetupRequired webAppUrl={config.webAppUrl} onBack={toSignIn} />
      </GatewayShell>
    );
  }
  if (step.kind === 'sign_in') {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<CircleCheck />}
          variant="success"
          title="Your account is ready"
          description={`Sign in as ${email} with your new password to open the Lookout workspace.`}
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" asChild>
            <Link to="/login">Sign in</Link>
          </Button>
        </GatewayActions>
      </GatewayShell>
    );
  }

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent title="Join Lookout" description={`Set up your account for ${email}.`}>
        <Form {...form}>
          <form
            noValidate
            aria-label="Set up your account"
            className="flex flex-col gap-4 text-left"
            onSubmit={form.handleSubmit((values) =>
              accept.mutate(values, { onSuccess: (_answer, submitted) => signInNow(submitted.password) }),
            )}
          >
            {accept.error ? (
              <Callout variant="error" role="alert">
                {accept.error.message}
              </Callout>
            ) : null}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} size="lg" autoComplete="name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" size="lg" autoComplete="new-password" />
                  </FormControl>
                  <FormDescription className="text-xs">{PASSWORD_RULE_HINT}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" size="lg" autoComplete="new-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
              {accept.isPending ? 'Setting up…' : busy ? 'Signing in…' : 'Create account'}
            </Button>
          </form>
        </Form>
      </GatewayContent>
    </GatewayShell>
  );
}
